import { useState, useEffect, useCallback } from 'react';
import { loadComments, addComment, updateComment, deleteComment, CommentEntityType, EntityComment } from '../../services/CommentsService';
import { useNotifications } from '../NotificationProvider';

/**
 * All four CommentThread wiring call sites (post/delete/edit + load-on-id-
 * change) were identical boilerplate copy-pasted per entity — this is that
 * logic once. entityId is undefined for an unsaved/new record; the thread
 * just stays empty until it has a real id to attach to.
 */
export function useEntityComments(entityType: CommentEntityType, entityId: string | undefined) {
  const { showError } = useNotifications();
  const [comments, setComments] = useState<EntityComment[]>([]);

  useEffect(() => {
    if (!entityId) { setComments([]); return; }
    loadComments(entityType, entityId).then(setComments).catch(() => { /* comments are supplementary — fail quietly */ });
  }, [entityType, entityId]);

  const handlePost = useCallback(async (body: string) => {
    if (!entityId) return;
    try {
      const created = await addComment(entityType, entityId, body);
      setComments(prev => [...prev, created]);
    } catch (err) {
      showError('Failed to post comment. Please try again.');
      throw err;
    }
  }, [entityType, entityId, showError]);

  const handleDelete = useCallback(async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      showError('Failed to delete comment. Please try again.');
    }
  }, [showError]);

  const handleEdit = useCallback(async (commentId: string, body: string) => {
    try {
      const updated = await updateComment(commentId, body);
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
    } catch (err) {
      showError('Failed to save comment. Please try again.');
      throw err;
    }
  }, [showError]);

  return { comments, handlePost, handleDelete, handleEdit };
}
